import { useEffect, useState, useCallback, useMemo } from "react";
import { debounce } from "lodash";
import { Button } from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { SearchInput } from "../../../components/ui/search-input";
import greetingService from "../../../services/GreetingService";
import { FormDialog } from "../../../components/ui/form-dialog";
import CommonFormNew from "../../../components/commonFormNew/commonFormNew";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { dateFormate } from "utils/utils";
import { Edit2Icon, Trash2, MoreVertical } from "lucide-react";
import { toast, ToastContainer } from 'react-toastify';
import { NewPagination } from "../../../components/ui/newPagination";
import Tooltip from "@mui/material/Tooltip";

export default function Greeting() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isAddGreetingOpen, setIsAddGreetingOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingGreetingId, setEditingGreetingId] = useState(null);
  const [formData, setFormData] = useState({
    greeting: "",
    date: null,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Form validation function
  function isFormValid(form, fields) {
    return fields.every((field) => {
      if (!field.required) return true;

      const fieldValue = form[field.name];

      switch (field.type) {
        case 'input':
          return fieldValue && fieldValue.trim() !== "";
        case 'date':
          return fieldValue !== null && fieldValue !== undefined;
        default:
          return fieldValue !== null && fieldValue !== undefined && fieldValue !== "";
      }
    });
  }

  const fetchGreetings = useCallback(async (search, page, limit) => {
    try {
      let query = {};
      if (search) {
        query.search = encodeURIComponent(search.trim());
      }
      query.page = page || 1;
      query.limit = limit || 10;
      
      const res = await greetingService.getGreetingList(query);
      const headers = res?.data?.header || [];
      const body = res?.data?.body || [];
      setTotalItems(res?.data?.totalCount || 0);

      const dynamicColumns = headers
        .filter(({ key }) => key !== "icon") // Exclude icon column as we'll add actions
        .map(({ key, label }) => {
          if (key === "date") {
            return {
              accessorKey: key,
              header: label,
              cell: ({ row }) => {
                const date = row.original.date;
                // return date ? dayjs(date).format("DD/MM/YYYY") : "-";
                return date;
              },
            };
          }
          if (key === "greeting") {
            return {
              accessorKey: key,
              header: label,
              cell: ({ row }) => {
                const greeting = row.original.greeting;
                return (
                  <Tooltip title={greeting || ""} arrow placement="bottom">
                  <div title={greeting} className="truncate max-w-[300px]">
                    {greeting}
                  </div>
                  </Tooltip>
                );
              },
            };
          }
          return {
            accessorKey: key,
            header: label,
          };
        });

      const actionsColumn = {
        accessorKey: "actions",
        header: "Action",
        cell: ({ row }) => (
          <div className="flex justify-end pr-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 cursor-pointer hover:bg-gray-100 focus:outline-none focus:ring-0"
                >
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="bottom"
                sideOffset={0}
                className="bg-white border border-gray-200 shadow-lg rounded-[8px] min-w-[120px] p-1"
              >
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50 px-3 py-2 text-sm text-gray-700 rounded-md transition-colors duration-150 focus:outline-none"
                  style={{
                    letterSpacing: "0.00938em",
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: '"Poppins", sans-serif'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFormData({
                      greeting: row.original.greeting,
                      date: row.original.date ? dateFormate(row.original.date, "YYYY-MM-DD") : null,
                    });
                    setEditingGreetingId(row.original.id);
                    setIsEditing(true);
                    setIsAddGreetingOpen(true);
                  }}
                >
                  <span className="flex items-center"><Edit2Icon className="h-4 w-4" /></span>
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer hover:bg-gray-50 focus:bg-gray-50 px-3 py-2 text-sm text-gray-700 rounded-md transition-colors duration-150 focus:outline-none"
                  style={{
                    letterSpacing: "0.00938em",
                    fontSize: "16px",
                    fontWeight: 500,
                    fontFamily: '"Poppins", sans-serif'
                  }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await greetingService.deleteGreeting(row.original.id);
                      toast.success(`Greeting deleted successfully`);
                      fetchGreetings(searchQuery, currentPage, itemsPerPage);
                    } catch (error) {
                      toast.error(error.response?.data?.message || "Failed to delete greeting");
                    }
                  }}
                >
                  <span className="flex items-center"><Trash2 className="h-4 w-4" /></span>
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      };

      setColumns([...dynamicColumns, actionsColumn]);
      setFilteredData(body);
    } catch (error) {
      console.error("Error fetching greetings:", error);
      toast.error("Failed to fetch greetings");
    }
  }, [currentPage, itemsPerPage, searchQuery]);

  const handleAddGreeting = async () => {
    try {
      const payload = {
        greeting: formData.greeting,
        date: formData.date ? dateFormate(formData.date, "YYYY-MM-DD") : "",
        isB2B: true
      };

      if (isEditing && editingGreetingId) {
        await greetingService.updateGreeting(editingGreetingId, payload);
        toast.success("Greeting updated successfully");
      } else {
        await greetingService.createGreeting(payload);
        toast.success("Greeting added successfully");
      }

      setFormData({ 
        greeting: "", 
        date: null,
      });
      setIsEditing(false);
      setEditingGreetingId(null);
      setIsAddGreetingOpen(false);
      fetchGreetings(searchQuery, currentPage, itemsPerPage);
    } catch (error) {
      toast.error(error.response?.data?.message || (isEditing ? "Failed to update greeting" : "Failed to add greeting"));
    }
  };

  const handleCancel = () => {
    setFormData({ 
      greeting: "", 
      date: null,
    });
    setIsEditing(false);
    setEditingGreetingId(null);
    setIsAddGreetingOpen(false);
  };

  // Debounced search
  const debouncedFetchGreetings = useMemo(
    () =>
      debounce((query) => {
        setCurrentPage(1);
        fetchGreetings(query, 1, itemsPerPage);
      }, 500),
    [fetchGreetings, itemsPerPage]
  );

  const handleSearch = (query) => {
    setSearchQuery(query || "");
    debouncedFetchGreetings(query);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchGreetings(searchQuery, page, itemsPerPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    fetchGreetings(searchQuery, 1, newItemsPerPage);
  };

  useEffect(() => {
    fetchGreetings(searchQuery, currentPage, itemsPerPage);
  }, [searchQuery, currentPage, itemsPerPage, fetchGreetings]);

  useEffect(() => {
    return () => {
      debouncedFetchGreetings.cancel();
    };
  }, [debouncedFetchGreetings]);

  // Field configurations
  const greetingFields = [
    { name: "greeting", label: "Greeting", type: "input", required: true, placeholder: "Enter Greeting", maxLength: 100, gridSize: 12 },
    { name: "date", label: "Date", type: "date", required: true, placeholder: "Select Date", gridSize: 12 }
  ];

  const isValid = isFormValid(formData, greetingFields);

  return (
    <div className="mx-auto">
      <div className="mb-4 flex justify-end">
        <div className="flex items-center gap-2">
          <SearchInput onSearch={handleSearch} placeholder={"Greeting Name"} className="w-[280px]" />
          
          <FormDialog
            trigger={
              <Button className="h-[40px] bg-[#ffa726] hover:bg-[#ffa726] rounded-[5px] font-normal text-[14px] text-white border border-white capitalize">
                + Add Greeting
              </Button>
            }
            title={isEditing ? "Edit" : "Add"}
            subtitle="Greeting"
            isOpen={isAddGreetingOpen}
            onOpenChange={setIsAddGreetingOpen}
            onSubmit={handleAddGreeting}
            onCancel={handleCancel}
            submitLabel={isEditing ? "Update" : "Create"}
            submitDisabled={!isValid}
            hideCancel={true}
          > 
           <LocalizationProvider dateAdapter={AdapterDayjs}>
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-2">
              Greetings must be configured at least one day in advance. Please select tomorrow or a later date.
            </p>
            <CommonFormNew
              config={greetingFields}
              formData={formData}
              onFormDataChange={setFormData}
            />
           </LocalizationProvider>
          </FormDialog>
        </div>
      </div>
      <div>
        <DataTable
          columns={columns}
          data={filteredData}
          onRowClick={(row) => console.log("Row clicked:", row)}
          pageSize={itemsPerPage}
        />
        <NewPagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
      <ToastContainer />
    </div>
  );
}